import { CONTRIBUTION_TIERS } from "../lib/constants";
import { h } from "../lib/dom";
import { formatCents, formatDateTime } from "../lib/format";
import {
  loadContributionsForRequest,
  openRefundModal,
  renderLedgerList,
} from "../lib/ledger";
import { supabase, type ReviewRequestRow } from "../lib/supabase";

const REQUEST_STATUSES: ReviewRequestRow["status"][] = [
  "new",
  "queued",
  "scheduled",
  "reviewed",
  "rejected",
  "pending_payment",
];

export function renderRequests(): HTMLElement {
  const listEl = h("div", { class: "rows" }, h("p", { class: "rows-empty" }, "Loading submissions…"));

  void load(listEl);

  return h(
    "main",
    { class: "page" },
    h(
      "header",
      { class: "page-head" },
      h(
        "div",
        {},
        h("p", { class: "eyebrow" }, "Reader submissions"),
        h("h1", { class: "page-title" }, "Submissions inbox"),
      ),
    ),
    h(
      "p",
      { class: "section-hint" },
      "Submissions from the public Request a Review form. Click a row for full details, payment info, contribution ledger entries, and internal notes.",
    ),
    listEl,
  );
}

async function load(listEl: HTMLElement): Promise<void> {
  const { data, error } = await supabase
    .from("review_requests")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(200);

  if (error) {
    listEl.replaceChildren(
      h("p", { class: "rows-error" }, `Could not load submissions: ${error.message}`),
    );
    return;
  }

  const rows = (data ?? []) as ReviewRequestRow[];
  if (rows.length === 0) {
    listEl.replaceChildren(h("p", { class: "rows-empty" }, "No submissions yet."));
    return;
  }

  listEl.replaceChildren(
    ...rows.map((row) =>
      h(
        "button",
        {
          class: "row request-row",
          type: "button",
          onclick: () => openSubmissionModal(row, () => void load(listEl)),
        },
        h(
          "div",
          { class: "row-main" },
          h("p", { class: "row-title" }, row.restaurant_name),
          h("p", { class: "row-sub" }, row.address || "No address provided"),
          row.reason ? h("p", { class: "request-reason request-reason-preview" }, row.reason) : null,
        ),
        h(
          "div",
          { class: "row-meta" },
          h(
            "span",
            { class: `row-status${statusClass(row)}` },
            paymentLabel(row),
          ),
          h(
            "span",
            { class: "row-score" },
            row.contribution_cents > 0 ? formatCents(row.contribution_cents) : "Free",
          ),
          h("span", { class: "row-date" }, formatDateTime(row.created_at)),
          h("span", { class: "row-email" }, `${row.requester_name} · ${row.requester_email}`),
        ),
      ),
    ),
  );
}

function statusClass(row: ReviewRequestRow): string {
  if (row.status === "reviewed") return " is-reviewed";
  if (row.status === "rejected") return " is-rejected";
  if (row.payment_status === "paid") return " is-paid";
  return "";
}

function paymentLabel(row: ReviewRequestRow): string {
  const tier = CONTRIBUTION_TIERS.find((t) => t.key === row.tier);
  let tierBit = tier ? tier.label : row.tier;
  if (row.tier === "custom" && row.contribution_cents > 0) {
    tierBit = `Custom (${formatCents(row.contribution_cents)})`;
  }
  if (row.payment_status === "paid") return `${tierBit} · Paid`;
  if (row.payment_status === "pending") return `${tierBit} · Awaiting pay`;
  if (row.payment_status === "failed") return `${tierBit} · Pay failed`;
  return tierBit;
}

function tierDetail(row: ReviewRequestRow): string {
  const tier = CONTRIBUTION_TIERS.find((t) => t.key === row.tier);
  if (!tier) return row.tier;
  if (row.tier === "custom" && row.contribution_cents > 0) {
    return `${tier.label} · ${formatCents(row.contribution_cents)}`;
  }
  return `${tier.label} · ${tier.amountLabel}`;
}

function openSubmissionModal(row: ReviewRequestRow, onUpdated: () => void): void {
  const existing = document.querySelector(".submission-modal-backdrop");
  existing?.remove();

  let saving = false;
  const statusEl = h("p", { class: "modal-status", "aria-live": "polite" });

  const statusSelect = h("select", { class: "input", id: "submission-status" }) as HTMLSelectElement;
  for (const value of REQUEST_STATUSES) {
    statusSelect.appendChild(
      h("option", { value, selected: row.status === value }, formatStatus(value)),
    );
  }

  const notesInput = h("textarea", {
    class: "input",
    id: "submission-notes",
    rows: 4,
    placeholder: "Internal notes — scheduling, visit plans, follow-up…",
  }) as HTMLTextAreaElement;
  notesInput.value = row.notes ?? "";

  const ledgerMount = h("div", { class: "ledger-mount" }, h("p", { class: "detail-muted" }, "Loading ledger…"));

  const close = () => {
    backdrop.remove();
    document.body.classList.remove("modal-open");
    document.removeEventListener("keydown", onKeydown);
  };

  const onKeydown = (event: KeyboardEvent) => {
    if (event.key === "Escape") close();
  };

  const saveBtn = h(
    "button",
    {
      class: "btn btn-primary",
      type: "button",
      onclick: () => void saveChanges(),
    },
    "Save changes",
  );

  async function saveChanges(): Promise<void> {
    if (saving) return;
    saving = true;
    saveBtn.setAttribute("disabled", "true");
    statusEl.textContent = "Saving…";
    statusEl.className = "modal-status";

    const nextStatus = statusSelect.value as ReviewRequestRow["status"];
    const nextNotes = notesInput.value.trim() || null;

    const { error } = await supabase
      .from("review_requests")
      .update({ status: nextStatus, notes: nextNotes })
      .eq("id", row.id);

    saving = false;
    saveBtn.removeAttribute("disabled");

    if (error) {
      statusEl.textContent = error.message;
      statusEl.className = "modal-status modal-status-error";
      return;
    }

    statusEl.textContent = "Saved.";
    statusEl.className = "modal-status modal-status-ok";
    row.status = nextStatus;
    row.notes = nextNotes;
    onUpdated();
    window.setTimeout(close, 450);
  }

  const panel = h(
    "div",
    {
      class: "modal-panel submission-modal",
      role: "dialog",
      "aria-modal": "true",
      "aria-labelledby": "submission-modal-title",
    },
    h(
      "header",
      { class: "modal-header" },
      h(
        "div",
        {},
        h("p", { class: "eyebrow" }, "Submission"),
        h("h2", { class: "modal-title", id: "submission-modal-title" }, row.restaurant_name),
        h("p", { class: "modal-subtitle" }, formatDateTime(row.created_at)),
      ),
      h(
        "button",
        {
          class: "modal-close",
          type: "button",
          "aria-label": "Close",
          onclick: close,
        },
        "×",
      ),
    ),
    h(
      "div",
      { class: "modal-body" },
      h(
        "div",
        { class: "modal-badges" },
        h("span", { class: `row-status${statusClass(row)}` }, formatStatus(row.status)),
        h("span", { class: "row-status" }, paymentLabel(row)),
      ),
      h(
        "section",
        { class: "modal-section" },
        h("h3", { class: "modal-section-title" }, "Restaurant"),
        detailGrid([
          ["Name", row.restaurant_name],
          [
            "Address",
            row.address
              ? mapsLink(row.address)
              : h("span", { class: "detail-muted" }, "Not provided"),
          ],
        ]),
      ),
      row.reason
        ? h(
            "div",
            { class: "submission-reason-block" },
            h("p", { class: "modal-section-title" }, "Why they want a review"),
            h("p", { class: "submission-reason-text" }, row.reason),
          )
        : null,
      h(
        "section",
        { class: "modal-section" },
        h("h3", { class: "modal-section-title" }, "Submitter"),
        detailGrid([
          ["Name", row.requester_name],
          [
            "Email",
            h(
              "a",
              { class: "detail-link", href: `mailto:${encodeURIComponent(row.requester_email)}` },
              row.requester_email,
            ),
          ],
        ]),
      ),
      h(
        "section",
        { class: "modal-section" },
        h("h3", { class: "modal-section-title" }, "Tier & payment"),
        detailGrid([
          ["Tier", tierDetail(row)],
          ["SLA", CONTRIBUTION_TIERS.find((t) => t.key === row.tier)?.sla ?? "—"],
          ["Contribution", row.contribution_cents > 0 ? formatCents(row.contribution_cents) : "None (free queue)"],
          ["Payment", formatPaymentStatus(row.payment_status)],
          ["Checkout session", row.stripe_checkout_session_id ?? "—"],
          ["Payment intent", row.stripe_payment_intent_id ?? "—"],
        ]),
      ),
      h(
        "section",
        { class: "modal-section" },
        h("h3", { class: "modal-section-title" }, "Contribution ledger"),
        h(
          "p",
          { class: "section-hint ledger-hint" },
          "Accounting records written when Stripe checkout completes. Used for disclosure and refunds.",
        ),
        ledgerMount,
      ),
      h(
        "section",
        { class: "modal-section" },
        h("h3", { class: "modal-section-title" }, "Consent"),
        h(
          "ul",
          { class: "consent-list" },
          consentItem("Independence acknowledged", row.consent_independence),
          consentItem("Non-negotiable score acknowledged", row.consent_non_negotiable),
          consentItem("Disclosure acknowledged", row.consent_disclosure),
        ),
      ),
      h(
        "section",
        { class: "modal-section" },
        h("h3", { class: "modal-section-title" }, "Internal"),
      h(
        "label",
        { class: "field", for: "submission-status" },
        h("span", { class: "label" }, "Queue status"),
        statusSelect,
      ),
      h(
        "label",
        { class: "field", for: "submission-notes" },
        h("span", { class: "label" }, "Notes"),
        notesInput,
      ),
      h(
        "p",
        { class: "modal-meta-id" },
        "ID ",
        h("code", {}, row.id),
      ),
      ),
    ),
    h(
      "footer",
      { class: "modal-footer" },
      statusEl,
      h(
        "div",
        { class: "modal-footer-actions" },
        h(
          "button",
          {
            class: "btn btn-ghost",
            type: "button",
            onclick: () => {
              close();
              window.location.hash = `/editor/new/from-request/${row.id}`;
            },
          },
          "Draft review",
        ),
        h("button", { class: "btn btn-ghost", type: "button", onclick: close }, "Close"),
        saveBtn,
      ),
    ),
  );

  const backdrop = h(
    "div",
    {
      class: "modal-backdrop submission-modal-backdrop",
      onclick: (event: MouseEvent) => {
        if (event.target === backdrop) close();
      },
    },
    panel,
  );

  document.body.appendChild(backdrop);
  document.body.classList.add("modal-open");
  document.addEventListener("keydown", onKeydown);
  panel.querySelector<HTMLElement>(".modal-close")?.focus();
  void loadContributionLedger(row.id, ledgerMount);
}

async function loadContributionLedger(requestId: string, mount: HTMLElement): Promise<void> {
  const refresh = () => void loadContributionLedger(requestId, mount);
  const { rows, error } = await loadContributionsForRequest(requestId);

  if (error) {
    mount.replaceChildren(
      h("p", { class: "ledger-error" }, `Could not load ledger: ${error}`),
    );
    return;
  }

  mount.replaceChildren(
    renderLedgerList(rows, {
      onEditRefund: (entry) => openRefundModal(entry, refresh),
    }),
  );
}

function detailGrid(items: [string, Child][]): HTMLElement {
  return h(
    "dl",
    { class: "detail-grid" },
    ...items.flatMap(([label, value]) => [
      h("dt", { class: "detail-label" }, label),
      h("dd", { class: "detail-value" }, value),
    ]),
  );
}

type Child = Node | string | number | null;

function mapsLink(address: string): HTMLElement {
  const q = encodeURIComponent(address);
  return h(
    "a",
    {
      class: "detail-link",
      href: `https://www.google.com/maps/search/?api=1&query=${q}`,
      target: "_blank",
      rel: "noopener noreferrer",
    },
    address,
  );
}

function consentItem(label: string, ok: boolean): HTMLElement {
  return h(
    "li",
    { class: ok ? "consent-item is-ok" : "consent-item is-missing" },
    ok ? "✓" : "✗",
    " ",
    label,
  );
}

function formatStatus(status: ReviewRequestRow["status"]): string {
  return status.replace(/_/g, " ");
}

function formatPaymentStatus(status: ReviewRequestRow["payment_status"]): string {
  if (status === "none") return "No payment (standard tier)";
  if (status === "pending") return "Awaiting checkout";
  if (status === "paid") return "Paid";
  if (status === "failed") return "Failed or abandoned";
  return status;
}
